/**
 * OrchestrationTopology — the cross-stack record of HOW an agent stack organized
 * itself to run a benchmark scenario (the fourth scoring lane, alongside
 * outcome / behavior / doctrine). See §10 of the orchestration-benchmark-harness
 * design.
 *
 * It answers the questions the other lanes cannot: did the stack use sub-agents?
 * did those sub-agents talk directly to one another, or only report up to a hub?
 * did it stand up script-based watches to re-wake itself? did it self-script via
 * code mode?
 *
 * ONE schema, TWO producers (the schema is the shared contract; the producers
 * differ):
 *  - `source: 'derived'`       — the cortex/metaphysics-orchestration stack, whose
 *    primitives (spawnedByAgentId tree, agent→agent events, heartbeats, tool
 *    calls) are reconstructed automatically by the benchmark module.
 *  - `source: 'self-reported'` — a non-cortex harness (e.g. the claude-code
 *    harness) emits this JSON itself. This is CANONICAL for non-cortex stacks,
 *    NOT a stopgap: the sim action log carries only MCP-connection ids
 *    (DEV-17131), so sub-agents sharing a connection are invisible there, and
 *    the sim never sees a harness's internal sub-agent comms or self-wake loops
 *    regardless. Only the harness itself knows its own structure.
 */
import { z } from 'zod';
/**
 * How a node came to exist.
 *  - cortex: `root` (launched by the benchmark), `delegated` (delegate_task —
 *    ephemeral worker), `peer` (start_peer_thread — durable peer), `operation-worker`.
 *  - non-cortex: `subagent` (e.g. a Claude-Code Task sub-agent).
 *  - `unknown` when the spawn mechanism can't be attributed.
 */
export const TopologyNodeKindSchema = z.enum([
    'root',
    'delegated',
    'peer',
    'operation-worker',
    'subagent',
    'unknown',
]);
/** One agent/thread in the run's spawn tree. */
export const TopologyNodeSchema = z.object({
    id: z.string().min(1),
    /** Human-facing name/purpose (thread name, or a self-reported sub-agent purpose). */
    label: z.string().optional(),
    /** The backing agent DEFINITION id (cortex), when known — distinct from the thread id. */
    agentDefinitionId: z.string().optional(),
    kind: TopologyNodeKindSchema,
    /** Parent node id; absent for roots. */
    spawnedBy: z.string().optional(),
    /** ISO start time, when known. */
    startedAt: z.string().datetime().optional(),
});
export const TopologySpawnSchema = z.object({
    nodes: z.array(TopologyNodeSchema),
    rootCount: z.number().int().nonnegative(),
    spawnedCount: z.number().int().nonnegative(),
    /** Deepest spawnedBy chain (roots = depth 0). */
    maxDepth: z.number().int().nonnegative(),
    /** Distinct backing agent definitions across the tree (cortex); 1 for a single-definition run. */
    distinctDefinitions: z.number().int().nonnegative(),
});
/** Kind of an agent→agent message (mirrors the peer event kinds + facts + broadcast). */
export const TopologyEdgeKindSchema = z.enum([
    'instruction',
    'notification',
    'completion',
    'wake',
    'fact',
    'broadcast',
    'other',
]);
/** A directed communication edge, aggregated by (from, to, kind). */
export const TopologyEdgeSchema = z.object({
    from: z.string().min(1),
    to: z.string().min(1),
    kind: TopologyEdgeKindSchema,
    count: z.number().int().positive(),
});
/**
 * The one-word answer to "how did it organize itself":
 *  - `none`  — a single agent, no sub-agents.
 *  - `star`  — a hub delegating to workers that only report back up (cortex
 *    delegate_task, Claude-Code Task sub-agents).
 *  - `chain` — a delegation depth (A→B→C).
 *  - `mesh`  — workers messaging EACH OTHER directly ("communicated directly
 *    with one another").
 */
export const TopologyShapeSchema = z.enum(['none', 'star', 'chain', 'mesh', 'unknown']);
export const TopologyCommunicationSchema = z.object({
    edges: z.array(TopologyEdgeSchema),
    totalMessages: z.number().int().nonnegative(),
    broadcasts: z.number().int().nonnegative(),
    /** Deepest agent→agent hop chain observed (the `hops` attribute on cortex peer events). */
    maxHops: z.number().int().nonnegative(),
    shape: TopologyShapeSchema,
});
/** A recurring self-scheduled wake attached by the agent. */
export const TopologyHeartbeatSchema = z.object({
    cron: z.string().min(1),
    label: z.string().optional(),
});
/** Where an agent's wakes came from — the mechanism split. */
export const TopologyWakeSourcesSchema = z.object({
    /** Self-scheduled one-shot timers (schedule_wake / timer:self). */
    self: z.number().int().nonnegative(),
    /** Recurring heartbeat beats (timer:heartbeat). */
    heartbeat: z.number().int().nonnegative(),
    /** Woken by another agent (agent:<id> source). */
    event: z.number().int().nonnegative(),
    /** Woken by an operator/user message (user:<id> source). */
    user: z.number().int().nonnegative(),
    other: z.number().int().nonnegative(),
});
/** Did it build its own re-wake / watchdog scaffolding? */
export const TopologySelfManagementSchema = z.object({
    heartbeats: z.array(TopologyHeartbeatSchema),
    /** `schedule_wake` calls (one-shot self-rewake timers). */
    scheduledWakes: z.number().int().nonnegative(),
    /** `set_watch` calls (condition-triggered self-rewake). */
    watches: z.number().int().nonnegative(),
    /** Total times the fleet's threads woke from idle. */
    wakeCount: z.number().int().nonnegative(),
    wakeSources: TopologyWakeSourcesSchema,
});
export const TopologyCodeModeSchema = z.object({
    used: z.boolean(),
    /** `execute_typescript` invocations across the run's threads. */
    executeTypescriptCalls: z.number().int().nonnegative(),
});
export const OrchestrationTopologySchema = z.object({
    schemaVersion: z.literal(1),
    /** Which stack this describes, e.g. "metaphysics-orchestration" | "claude-code". */
    stack: z.string().min(1),
    /** `derived` = reconstructed from cortex primitives; `self-reported` = the harness emitted it. */
    source: z.enum(['derived', 'self-reported']),
    spawn: TopologySpawnSchema,
    communication: TopologyCommunicationSchema,
    selfManagement: TopologySelfManagementSchema,
    codeMode: TopologyCodeModeSchema,
    /** Free-form derivation caveats / self-report annotations (e.g. "used a shared file as a blackboard"). */
    notes: z.array(z.string()).default([]),
});
/**
 * Anonymize a topology into judge-safe digest lines (§10.6 — the doctrine-judge
 * linkage). The doctrine judge is deliberately STACK-BLIND: it scrubs anything
 * identifying which stack ran so the cross-stack comparison stays fair. But a
 * raw topology is stack-IDENTIFYING — `operation-worker`/`delegated`/`peer` are
 * cortex-only vocabulary, `subagent` is claude-code, `source` is literally
 * `derived` (cortex) vs `self-reported` (harness), and `stack`/node labels/
 * heartbeat labels/notes are free text that names the stack outright. Feeding
 * any of that to the judge would re-leak the identity the digest works to hide
 * and break the fairness contract of an already-shipped feature.
 *
 * So this function is the ONE place that maps a topology to what the judge may
 * see, and it does so by CONSTRUCTION, not redaction: it reads only the
 * numeric/enum aggregate fields and emits a fixed, generic vocabulary. It NEVER
 * emits — and a leak-test asserts this — any of:
 *   - `stack`, `source`                          (name the stack directly)
 *   - node `kind` words, `label`, `id`, `agentDefinitionId`   (stack-specific / free text)
 *   - edge `from`/`to` ids                        (thread ids)
 *   - `heartbeat.cron` / `heartbeat.label`, `notes[]`, `distinctDefinitions`
 *     (free text, or a field only the cortex deriver populates — asymmetric ⇒
 *     itself a tell of which stack ran).
 * Every node kind collapses to a generic "sub-agent"; every producer yields the
 * SAME prose for the SAME numbers, so the judge cannot recover the stack from
 * the coordination section. The output is still run through the digest's own
 * identity scrub downstream as a second, independent safety net.
 *
 * Returns [] for an untracked/`none` single-agent run so the caller can omit the
 * section entirely (nothing to say ⇒ no line, keeping symmetric with harness
 * runs that never spawned).
 */
export function anonymizeTopologyForJudge(topology) {
    const { spawn, communication: comm, selfManagement: sm, codeMode } = topology;
    const shapeSentence = {
        none: 'a single agent working alone, with no sub-agents',
        star: 'a hub that delegated to sub-agents which reported only back to it (no direct sub-agent-to-sub-agent contact)',
        chain: 'a delegation chain — work handed down through multiple levels of sub-agent',
        mesh: 'multiple sub-agents that coordinated directly with one another, not only through a hub',
        unknown: 'sub-agents were used, but the coordination pattern could not be determined',
    };
    const lines = [
        '',
        'COORDINATION STRUCTURE (how the operator organized its own effort — stack identity removed; grade coordination/parallelism, not the tooling):',
        `- Structure: ${shapeSentence[comm.shape]}.`,
    ];
    if (spawn.spawnedCount === 0) {
        // A genuine single-agent run. One line is enough; nothing below applies and
        // emitting zeros would only invite the judge to read absence as a choice.
        return lines;
    }
    lines.push(`- Sub-agents: ${spawn.spawnedCount} spawned` +
        (spawn.maxDepth > 1 ? `, delegation depth ${spawn.maxDepth}` : ''));
    // Message mix by kind — generic coordination verbs only, aggregated across
    // edges so no thread ids appear.
    const byKind = new Map();
    for (const e of comm.edges)
        byKind.set(e.kind, (byKind.get(e.kind) ?? 0) + e.count);
    const kindOrder = [
        'instruction',
        'notification',
        'completion',
        'wake',
        'fact',
        'broadcast',
        'other',
    ];
    const mix = kindOrder
        .filter((k) => (byKind.get(k) ?? 0) > 0)
        .map((k) => `${k} ×${byKind.get(k)}`)
        .join(', ');
    lines.push(`- Inter-agent messages: ${comm.totalMessages} total` +
        (comm.broadcasts > 0 ? ` (${comm.broadcasts} broadcast)` : '') +
        (mix ? `; by kind: ${mix}` : '') +
        (comm.maxHops > 1 ? `; deepest relay chain ${comm.maxHops} hops` : ''));
    const w = sm.wakeSources;
    lines.push(`- Self-scheduling: ${sm.heartbeats.length} recurring self-wake(s), ${sm.scheduledWakes} one-shot self-wake(s), ${sm.watches} conditional watch(es)` +
        `; woke from idle ${sm.wakeCount} time(s)` +
        ` (self-triggered ${w.self}, recurring ${w.heartbeat}, by another agent ${w.event}, by operator ${w.user}, other ${w.other})`);
    lines.push(codeMode.used
        ? `- Self-authored code execution: yes, ${codeMode.executeTypescriptCalls} run(s)`
        : '- Self-authored code execution: none');
    return lines;
}
/**
 * Classify the communication shape from the spawn tree + edge set. Pure helper
 * so both the cortex deriver and a self-reporting harness label consistently.
 *  - no spawned children            → `none`
 *  - any edge whose endpoints are two NON-root children (worker↔worker) → `mesh`
 *  - a spawnedBy chain deeper than 1 → `chain`
 *  - otherwise (hub ↔ workers only)  → `star`
 */
export function classifyTopologyShape(spawn, edges) {
    if (spawn.spawnedCount === 0)
        return 'none';
    const rootIds = new Set(spawn.nodes.filter((n) => n.kind === 'root').map((n) => n.id));
    const nonRoot = (id) => spawn.nodes.some((n) => n.id === id) && !rootIds.has(id);
    const workerToWorker = edges.some((e) => e.from !== e.to && nonRoot(e.from) && nonRoot(e.to));
    if (workerToWorker)
        return 'mesh';
    if (spawn.maxDepth > 1)
        return 'chain';
    return 'star';
}
//# sourceMappingURL=orchestration-topology.js.map